// Searchable brand/studio picker with inline creation.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronUpDownIcon, PlusIcon } from "@heroicons/react/20/solid";
import { Entity } from "../../types/cms";
import { createEntity, ENTITY_LABEL, EntityKind } from "../../lib/videoAdmin";
import { firstLogo } from "../../lib/catalog";
import { normalizeName } from "../../lib/enrich";
import { notify } from "../../utils/notify";
import { Field, inputClass, Modal, primaryButton, secondaryButton } from "./ui";

interface Props {
  label: string;
  kind: EntityKind;
  companies: Entity[];
  agencies: Entity[];
  value: string;
  onChange: (id: string) => void;
  onCreated: (entity: Entity, kind: EntityKind) => void;
  /** Shown under the field, e.g. why a value was pre-selected. */
  hint?: React.ReactNode;
  /** Default name offered when creating (e.g. the YouTube channel). */
  suggestedName?: string;
  error?: string;
}

export default function EntityPicker({
  label,
  kind,
  companies,
  agencies,
  value,
  onChange,
  onCreated,
  hint,
  suggestedName,
  error,
}: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = `entity-options-${kind}-${label.replace(/\W+/g, "-")}`;

  // The expected collection first; the other one stays selectable because
  // legacy videos sometimes point to it.
  const options = useMemo(() => {
    const primary = (kind === "companies" ? companies : agencies).map((e) => ({ entity: e, kind }));
    const otherKind: EntityKind = kind === "companies" ? "agencies" : "companies";
    const secondary = (kind === "companies" ? agencies : companies).map((e) => ({ entity: e, kind: otherKind }));
    return [...primary, ...secondary];
  }, [kind, companies, agencies]);

  const selected = options.find((o) => o.entity.id === value);
  const q = normalizeName(query);
  const filtered = q
    ? options.filter((o) => normalizeName(o.entity.name).includes(q))
    : options;

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (id: string) => {
    onChange(id);
    setQuery("");
    setOpen(false);
  };

  return (
    <Field label={label} hint={hint} error={error}>
      <div ref={containerRef} className="relative">
        <div className="relative">
          {selected && !open && firstLogo(selected.entity) && (
            <img
              src={firstLogo(selected.entity)}
              alt=""
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded object-contain"
            />
          )}
          <input
            className={`${inputClass} pr-9 ${selected && !open && firstLogo(selected.entity) ? "pl-9" : ""} ${error ? "border-red-400" : ""}`}
            value={open ? query : selected?.entity.name ?? ""}
            placeholder={`Rechercher une ${ENTITY_LABEL[kind]}…`.replace("une studio", "un studio")}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (filtered[0]) pick(filtered[0].entity.id);
              }
              if (e.key === "Escape") setOpen(false);
            }}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
          />
          <ChevronUpDownIcon className="w-4 h-4 text-neutral-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        {open && (
          <div id={listId} className="absolute z-[1000] mt-1 w-full bg-white border border-neutral-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-neutral-900 bg-neutral-50 border-b border-neutral-200 hover:bg-neutral-100 sticky top-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setOpen(false);
                setCreating(true);
              }}
            >
              <PlusIcon className="w-4 h-4" />
              {query.trim() ? `Créer « ${query.trim()} »` : `Créer ${kind === "companies" ? "une marque" : "un studio"}`}
            </button>
            {filtered.length === 0 && (
              <p className="px-3 py-3 text-sm text-neutral-500">Aucun résultat.</p>
            )}
            {filtered.map(({ entity, kind: k }) => (
              <button
                type="button"
                key={`${k}-${entity.id}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-neutral-100 ${
                  entity.id === value ? "font-semibold" : ""
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(entity.id)}
              >
                {firstLogo(entity) ? (
                  <img src={firstLogo(entity)} alt="" className="w-5 h-5 rounded object-contain" />
                ) : (
                  <span className="w-5 h-5 rounded bg-neutral-200" />
                )}
                <span className="truncate flex-1">{entity.name}</span>
                {k !== kind && (
                  <span className="text-xs text-neutral-500">{k === "companies" ? "marque" : "studio"}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      <EntityCreateModal
        open={creating}
        kind={kind}
        initialName={query.trim() || suggestedName || ""}
        onClose={() => setCreating(false)}
        onCreated={(entity) => {
          onCreated(entity, kind);
          pick(entity.id);
          setCreating(false);
        }}
      />
    </Field>
  );
}

export function EntityCreateModal({
  open,
  kind,
  initialName = "",
  onClose,
  onCreated,
}: {
  open: boolean;
  kind: EntityKind;
  initialName?: string;
  onClose: () => void;
  onCreated: (entity: Entity) => void;
}) {
  const [name, setName] = useState(initialName);
  const [websiteURL, setWebsiteURL] = useState("");
  const [description, setDescription] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setWebsiteURL("");
      setDescription("");
      setLogoFile(null);
    }
  }, [open, initialName]);

  const noun = kind === "companies" ? "marque" : "studio";

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const entity = await createEntity(kind, { name, websiteURL, description, logoFile });
      notify({ isSuccess: true, message: `${kind === "companies" ? "Marque créée" : "Studio créé"} : ${entity.name}` });
      onCreated(entity);
    } catch (err) {
      notify({
        isSuccess: false,
        message: `Création ${kind === "companies" ? "de la marque" : "du studio"} impossible`,
        info: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={kind === "companies" ? "Nouvelle marque" : "Nouveau studio"}
      footer={
        <>
          <button type="button" className={secondaryButton} onClick={onClose}>
            Annuler
          </button>
          <button type="button" className={primaryButton} disabled={!name.trim() || saving} onClick={submit}>
            {saving ? "Création…" : `Créer ${kind === "companies" ? "la marque" : "le studio"}`}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Nom" htmlFor="entity-name">
          <input id="entity-name" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Site web" htmlFor="entity-site" hint={`Sans logo fourni, le favicon du site sera utilisé.`}>
          <input
            id="entity-site"
            className={inputClass}
            value={websiteURL}
            onChange={(e) => setWebsiteURL(e.target.value)}
            placeholder="https://…"
          />
        </Field>
        <Field label="Description courte" htmlFor="entity-desc">
          <input id="entity-desc" className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label={`Logo de la ${noun}`.replace("la studio", "du studio")} htmlFor="entity-logo">
          <input
            id="entity-logo"
            type="file"
            accept="image/*"
            className="text-sm"
            onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
          />
        </Field>
      </div>
    </Modal>
  );
}
